/**
 * احراز هویت کاربر
 * =================
 * مشکلی که این صفحه پیش از این داشت: دکمه «درخواست ارتقا به سطح دو» بی‌درنگ
 * درخواست می‌فرستاد، سرور با یک پیام کوتاه ردش می‌کرد و **هیچ پنجره‌ای باز
 * نمی‌شد**. کاربر نه می‌دانست چه چیزی کم دارد، نه کجا باید تهیه‌اش کند، و
 * اصلاً راهی برای فرستادن مدرک نداشت.
 *
 * حالا کلیک روی دکمه یک پنجره باز می‌کند که:
 *
 * ۱. چک‌لیست شرط‌ها را با وضعیت واقعی هر بند نشان می‌دهد،
 * ۲. برای هر بندِ ناقص، لینک صفحه‌ای که باید برود می‌گذارد،
 * ۳. مدارک لازم را همان‌جا می‌گیرد،
 * ۴. و دکمه ثبت تا کامل‌شدن همه‌چیز غیرفعال می‌ماند.
 *
 * همان شرط‌ها را سرور هم بررسی می‌کند (`services/kyc.py`) — پس چیزی که اینجا
 * سبز است آنجا رد نمی‌شود.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ShieldCheck, Check, Lock, Upload, CreditCard, Video, X, Clock,
  AlertTriangle, Loader2, Image as ImageIcon, ArrowLeft,
} from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Button, Field, Rows, Modal } from '../components/UI'
import { useAuth, toast } from '../store'
import { get, post } from '../lib/api'
import { clsx, fa, num, toEnDigits } from '../lib/format'

const BASE = import.meta.env.VITE_API_URL || ''

type Doc = { kind: string; label: string; path: string; name: string; size: number }
type Req = {
  key: string; label: string; done: boolean | null
  hint: string; action: string; actionLabel: string
}

/**
 * آپلود یک مدرک.
 *
 * از `fetch` خام استفاده می‌شود نه از کمکی `api()`: آن کمکی همیشه هدر
 * `application/json` می‌گذارد و مرورگر برای `FormData` باید خودش هدر را با
 * مرز (boundary) بسازد.
 */
async function uploadDoc(file: File, kind: string): Promise<Doc> {
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  const token = localStorage.getItem('az_token')
  const res = await fetch(`${BASE}/api/user/kyc/documents`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  const text = await res.text()
  let data: any = null
  try { data = text ? JSON.parse(text) : null } catch { data = null }
  if (!res.ok) throw new Error((data && (data.detail ?? data.message)) || 'بارگذاری مدرک ناموفق بود')
  return data
}

export default function Kyc() {
  const { user, refresh } = useAuth()
  const [data, setData] = useState<any>(null)
  const [form, setForm] = useState({ full_name: '', national_id: '', mobile: '', birth_date: '', address: '' })
  const [loading, setLoading] = useState(false)
  const [want, setWant] = useState<number | null>(null)

  const load = () => get('/user/kyc').then((r) => {
    setData(r)
    setForm((f) => ({ ...f, full_name: r.fullName || '', national_id: r.nationalId || '', mobile: r.mobile || '' }))
  }).catch(() => {})
  // عمداً `() => { load() }` و نه `useEffect(load, [])`.
  //
  // `load` یک Promise برمی‌گرداند و ری‌اکت مقدار بازگشتی useEffect را «تابع
  // پاک‌سازی» فرض می‌کند. در حالت توسعه فقط هشدار می‌دهد، ولی در نسخه
  // production هنگام خروج از صفحه همان Promise را **صدا می‌زند** و
  // `destroy is not a function` پرتاب می‌شود — یعنی صفحه فقط روی سرور واقعی
  // و فقط موقع رفتن به صفحه بعد می‌شکست.
  useEffect(() => { load() }, [])

  const submit = async () => {
    setLoading(true)
    try {
      const r: any = await post('/user/kyc', form)
      toast.ok(r?.pending ? 'اطلاعات ثبت شد و در انتظار بررسی است' : 'اطلاعات هویتی ثبت و تأیید شد')
      await refresh(); load()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  const level = user?.kycLevel ?? 0
  const pendingLevel: number | null = data?.pendingLevel ?? null
  const levels: any[] = data?.levels ?? []

  return (
    <PanelShell title="احراز هویت" subtitle="با تکمیل هر سطح، سقف معاملات و برداشت شما افزایش می‌یابد">
      {!data ? <Rows n={4} h="h-24" /> : (
        <>
          <Card className="mb-5">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-2xl border border-brand-500/30 bg-brand-500/10 text-brand-300">
                <ShieldCheck className="h-6 w-6" />
              </span>
              <div>
                <div className="font-bold text-white">سطح فعلی: {fa(level)}</div>
                <div className="text-xs text-slate-400">
                  {level === 0 ? 'حساب شما هنوز فعال نشده است' : 'حساب شما تأیید شده است'}
                </div>
              </div>
            </div>

            <div className="relative flex items-center justify-between">
              <div className="absolute inset-x-6 top-5 h-0.5 bg-white/[.07]" />
              <div className="absolute right-6 top-5 h-0.5 bg-gradient-to-l from-brand-500 to-aqua transition-all duration-700"
                style={{ width: `calc(${(level / 3) * 100}% - ${level ? 0 : 24}px)` }} />
              {[0, 1, 2, 3].map((l) => (
                <div key={l} className="relative z-10 flex flex-1 flex-col items-center gap-2">
                  <span className={clsx('grid h-10 w-10 place-items-center rounded-full border-2 text-sm font-bold transition',
                    level >= l ? 'border-brand-400 bg-brand-500/25 text-white' : 'border-white/10 bg-ink-900 text-slate-600')}>
                    {level > l ? <Check className="h-4 w-4" /> : fa(l)}
                  </span>
                  <span className={clsx('text-[11px]', level >= l ? 'text-slate-300' : 'text-slate-600')}>
                    {l === 0 ? 'ثبت‌نام' : `سطح ${fa(l)}`}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* درخواست در جریان — پیش از این هیچ نشانی از آن در صفحه نبود و کاربر
              با دیدن دکمه، دوباره تلاش می‌کرد و خطای «درخواست قبلی…» می‌گرفت. */}
          {pendingLevel && (
            <Card className="mb-5 border-gold/25 bg-gold/[.04]">
              <div className="flex flex-wrap items-center gap-3">
                <Clock className="h-5 w-5 shrink-0 text-gold" />
                <div className="text-sm">
                  <div className="font-bold text-white">درخواست سطح {fa(pendingLevel)} در حال بررسی است</div>
                  <div className="mt-1 text-xs text-slate-400">
                    نتیجه معمولاً تا چند ساعت کاری اعلام می‌شود و با پیامک و اعلان به شما خبر می‌دهیم.
                    {(data.pendingRequest?.documents?.length ?? 0) > 0 && (
                      <> مدارک ارسالی: {data.pendingRequest.documents.map((d: any) => d.label).join('، ')}</>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {!pendingLevel && data.rejectReason && (
            <Card className="mb-5 border-down/25 bg-down/[.04]">
              <div className="flex flex-wrap items-center gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-down" />
                <div className="text-sm">
                  <div className="font-bold text-white">آخرین درخواست شما رد شد</div>
                  <div className="mt-1 text-xs text-slate-400">دلیل: {data.rejectReason}</div>
                </div>
              </div>
            </Card>
          )}

          <div className="grid gap-5 lg:grid-cols-3">
            {levels.map((lv: any) => {
              const done = level >= lv.level
              const canDo = level === lv.level - 1
              const checks: Req[] = lv.checklist ?? []
              return (
                <Card key={lv.level} className={clsx('relative', done && 'border-up/25')}>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-bold text-white">{lv.title}</h3>
                    {done ? <span className="chip border-up/30 bg-up/10 text-up"><Check className="h-3 w-3" />تأیید شده</span>
                      : pendingLevel === lv.level ? <span className="chip border-gold/30 bg-gold/10 text-gold"><Clock className="h-3 w-3" />در بررسی</span>
                        : <span className="chip text-slate-500"><Lock className="h-3 w-3" />قفل</span>}
                  </div>
                  <ul className="mb-4 space-y-2 text-xs text-slate-400">
                    {checks.map((r) => (
                      <li key={r.key} className="flex items-start gap-2">
                        {done || r.done === true
                          ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-up" />
                          : r.done === null
                            ? <ImageIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                            : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-600" />}
                        <span className={clsx(done || r.done === true ? 'text-slate-300' : '')}>{r.label}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-1.5 rounded-xl border border-white/[.06] bg-white/[.02] p-3 text-[11px]">
                    <div className="flex justify-between"><span className="text-slate-500">سقف برداشت روزانه</span>
                      <span className="num text-slate-200">{num(lv.limits.withdrawIrt)} تومان</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">سقف معاملات</span>
                      <span className="num text-slate-200">{num(lv.limits.tradeIrt)} تومان</span></div>
                  </div>
                  {!done && canDo && lv.level > 1 && !pendingLevel && (
                    <Button variant="ghost" className="mt-4 w-full text-xs" onClick={() => setWant(lv.level)}>
                      {lv.level === 2 ? <CreditCard className="h-4 w-4" /> : <Video className="h-4 w-4" />}
                      درخواست ارتقا به سطح {fa(lv.level)}
                    </Button>
                  )}
                  {!done && !canDo && lv.level > 1 && (
                    <p className="mt-4 text-center text-[11px] text-slate-600">
                      ابتدا سطح {fa(lv.level - 1)} را کامل کنید
                    </p>
                  )}
                </Card>
              )
            })}
          </div>

          {level < 1 && (
            <Card className="mt-5">
              <h3 className="mb-5 font-bold text-white">تکمیل اطلاعات هویتی (سطح ۱)</h3>
              <div className="grid gap-x-5 sm:grid-cols-2">
                <Field label="نام و نام خانوادگی">
                  <input className="input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                </Field>
                <Field label="کد ملی">
                  <input className="input num" dir="ltr" maxLength={10} value={form.national_id}
                    onChange={(e) => setForm({ ...form, national_id: toEnDigits(e.target.value).replace(/\D/g, '') })} />
                </Field>
                <Field label="شماره موبایل">
                  <input className="input num" dir="ltr" value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: toEnDigits(e.target.value).replace(/\D/g, '') })} />
                </Field>
                <Field label="تاریخ تولد (اختیاری)">
                  <input className="input num" dir="ltr" placeholder="1370/05/12" value={form.birth_date}
                    onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
                </Field>
              </div>
              <Field label="نشانی (اختیاری)">
                <textarea className="input min-h-[80px]" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>
              <p className="mb-4 text-[11px] leading-6 text-slate-500">
                نام و کد ملی باید دقیقاً با کارت ملی شما یکی باشد؛ این اطلاعات استعلام می‌شود و
                کارت بانکی‌ای که بعداً ثبت می‌کنید هم باید به همین نام باشد.
              </p>
              <Button loading={loading} onClick={submit} className="w-full !py-3">ثبت و تأیید اطلاعات</Button>
            </Card>
          )}

          {level >= 1 && (
            <Card className="mt-5">
              <h3 className="mb-4 font-bold text-white">اطلاعات ثبت‌شده</h3>
              <div className="grid gap-4 text-sm sm:grid-cols-3">
                {[['نام و نام خانوادگی', data.fullName], ['کد ملی', data.nationalId], ['شماره موبایل', data.mobile]].map(([k, v]) => (
                  <div key={k as string} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                    <div className="mb-1 text-[11px] text-slate-500">{k}</div>
                    <div className="num text-slate-200">{v || '—'}</div>
                  </div>
                ))}
              </div>
              {level < 2 && (
                <p className="mt-4 text-xs text-slate-400">
                  برای برداشت، ابتدا از صفحه <Link to="/wallet" className="text-brand-300 hover:underline">کیف پول</Link> یک کارت بانکی به نام خودتان ثبت کنید و سپس سطح ۲ را درخواست دهید.
                </p>
              )}
            </Card>
          )}

          <UpgradeModal
            level={want}
            info={levels.find((l) => l.level === want)}
            maxDocMb={data.maxDocMb ?? 8}
            onClose={() => setWant(null)}
            onDone={async () => { setWant(null); await refresh(); load() }}
          />
        </>
      )}
    </PanelShell>
  )
}

/* ═══════════════════════════════════════════════ پنجره درخواست ارتقا */
function UpgradeModal({
  level, info, maxDocMb, onClose, onDone,
}: {
  level: number | null
  info: any
  maxDocMb: number
  onClose: () => void
  onDone: () => void | Promise<void>
}) {
  const [docs, setDocs] = useState<Record<string, Doc>>({})
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => { setDocs({}); setNote('') }, [level])

  if (!level || !info) return null

  const checks: Req[] = info.checklist ?? []
  const needed: { kind: string; label: string }[] = info.documents ?? []
  const blockers = checks.filter((c) => c.done === false)
  const missingDocs = needed.filter((d) => !docs[d.kind])
  const ready = blockers.length === 0 && missingDocs.length === 0

  const send = async () => {
    setBusy(true)
    try {
      const r: any = await post(`/user/kyc/level/${level}`, {
        documents: Object.values(docs).map((d: Doc) => ({ kind: d.kind, path: d.path, name: d.name, size: d.size })),
        note,
      })
      toast.ok(r?.message || 'درخواست ثبت شد')
      await onDone()
    } catch (e: any) { toast.err(e.message) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title={`درخواست ارتقا به سطح ${fa(level)}`} wide>
      <div className="space-y-5">
        <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-3 text-[11px] leading-6 text-slate-400">
          پس از ثبت، مدارک شما را کارشناس بررسی می‌کند و نتیجه با پیامک و اعلان اعلام می‌شود.
          مدارک شما در بخش خصوصی نگهداری می‌شود و از نشانی عمومی قابل دیدن نیست.
        </div>

        {/* شرط‌ها */}
        <div>
          <h4 className="mb-2 text-xs font-bold text-slate-300">شرط‌های این سطح</h4>
          <div className="space-y-2">
            {checks.filter((c) => c.done !== null).map((c) => (
              <div key={c.key} className={clsx(
                'flex flex-wrap items-center gap-2 rounded-xl border p-3 text-xs',
                c.done ? 'border-up/20 bg-up/[.05]' : 'border-down/20 bg-down/[.05]')}>
                {c.done ? <Check className="h-4 w-4 shrink-0 text-up" /> : <X className="h-4 w-4 shrink-0 text-down" />}
                <span className={c.done ? 'text-slate-300' : 'text-white'}>{c.label}</span>
                {!c.done && c.hint && <span className="text-slate-400">— {c.hint}</span>}
                {!c.done && c.action && (
                  <Link to={c.action} onClick={onClose}
                    className="mr-auto flex items-center gap-1 rounded-lg border border-brand-500/30 bg-brand-500/10 px-2.5 py-1 text-brand-200 transition hover:bg-brand-500/20">
                    {c.actionLabel || 'انجام بده'}<ArrowLeft className="h-3 w-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* مدارک */}
        {needed.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-bold text-slate-300">مدارک لازم</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              {needed.map((d) => (
                <DocSlot key={d.kind} kind={d.kind} label={d.label} maxMb={maxDocMb}
                  value={docs[d.kind]}
                  onChange={(v) => setDocs((s) => {
                    const next = { ...s }
                    if (v) next[d.kind] = v; else delete next[d.kind]
                    return next
                  })} />
              ))}
            </div>
            <p className="mt-2 text-[11px] leading-6 text-slate-500">
              تصویر باید واضح و بدون برش گوشه‌ها باشد و همه متن آن خوانده شود. قالب‌های
              JPG، PNG، WebP و AVIF تا {fa(maxDocMb)} مگابایت پذیرفته می‌شود.
            </p>
          </div>
        )}

        <Field label="توضیح برای کارشناس (اختیاری)">
          <textarea className="input min-h-[70px]" value={note} maxLength={280}
            onChange={(e) => setNote(e.target.value)} />
        </Field>

        {!ready && (
          <div className="rounded-xl border border-gold/25 bg-gold/[.06] p-3 text-[11px] leading-6 text-slate-300">
            برای ثبت درخواست، این موارد باقی مانده است:{' '}
            {[...blockers.map((b) => b.label), ...missingDocs.map((d) => d.label)].join('، ')}
          </div>
        )}

        <div className="flex gap-3">
          <Button className="flex-1" loading={busy} disabled={!ready} onClick={send}>ثبت درخواست</Button>
          <Button variant="ghost" onClick={onClose}>انصراف</Button>
        </div>
      </div>
    </Modal>
  )
}

/* ─────────────────────────────────────────────── یک خانه بارگذاری مدرک */
function DocSlot({
  kind, label, maxMb, value, onChange,
}: {
  kind: string; label: string; maxMb: number
  value?: Doc; onChange: (v: Doc | null) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState('')

  // نشانی موقتِ پیش‌نمایش را باید آزاد کرد، وگرنه با هر انتخابِ دوباره یک
  // شیء در حافظه مرورگر جا می‌ماند.
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const pick = async (file?: File | null) => {
    if (!file) return
    if (file.size > maxMb * 1024 * 1024) {
      toast.err(`حجم فایل از ${fa(maxMb)} مگابایت بیشتر است`)
      return
    }
    setBusy(true)
    try {
      const saved = await uploadDoc(file, kind)
      if (preview) URL.revokeObjectURL(preview)
      setPreview(URL.createObjectURL(file))
      onChange(saved)
    } catch (e: any) { toast.err(e.message) } finally { setBusy(false) }
  }

  return (
    <div className={clsx('rounded-xl border p-3 transition',
      value ? 'border-up/25 bg-up/[.04]' : 'border-dashed border-white/15 bg-white/[.02]')}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-200">{label}</span>
        {value && (
          <button type="button" onClick={() => { onChange(null); setPreview('') }}
            className="rounded-lg p-1 text-slate-500 transition hover:bg-white/10 hover:text-down">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {value ? (
        <div className="flex items-center gap-3">
          {preview
            ? <img src={preview} alt={label} className="h-16 w-24 rounded-lg object-cover" />
            : <span className="grid h-16 w-24 place-items-center rounded-lg bg-white/[.04] text-slate-500"><ImageIcon className="h-5 w-5" /></span>}
          <div className="min-w-0 text-[11px]">
            <div className="truncate text-up">بارگذاری شد</div>
            <div className="num truncate text-slate-500" dir="ltr">{value.name}</div>
          </div>
        </div>
      ) : (
        <button type="button" disabled={busy} onClick={() => ref.current?.click()}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-4 text-xs text-slate-400 transition hover:bg-white/[.04] hover:text-slate-200">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? 'در حال بارگذاری…' : 'انتخاب تصویر'}
        </button>
      )}

      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/avif" className="hidden"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = '' }} />
    </div>
  )
}
